import { useEffect, useMemo } from "react";
import useMaterialStore from "../stores/materialStore";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "./ui/select";
// Helper function to build a hierarchical structure (excluding deactivated materials)
const buildHierarchy = (materials) => {
  const materialMap = new Map();

  // Initialize only active materials in the map
  materials.forEach((mat) => {
    if (mat.status !== "D") {
      // Only include active materials
      materialMap.set(mat._id, { ...mat, children: [], hasParent: false });
    }
  });

  const rootMaterials = [];

  // Assign children to their respective parents (only for active materials)
  materials.forEach((mat) => {
    if (
      mat.status !== "D" &&
      mat.sub_material?._id &&
      materialMap.has(mat.sub_material._id)
    ) {
      const parent = materialMap.get(mat.sub_material._id);
      const child = materialMap.get(mat._id);

      if (parent && child) {
        parent.children.push(child);
        child.hasParent = true;
      }
    }
  });

  // Only keep materials that are either parents or children
  materialMap.forEach((mat) => {
    if (mat.children.length > 0 || mat.hasParent) {
      if (!mat.hasParent) rootMaterials.push(mat);
    }
  });

  return rootMaterials;
};

// Recursive function to render materials with indentation (only active materials)
const renderMaterials = (materials, level = 0) => {
  return materials
    .filter((mat) => mat.status !== "D") // Additional filter as a safeguard
    .map((mat) => (
      <div key={mat._id}>
        {mat.children.length > 0 ? (
          // Material with children (Bold & Unselectable)
          <div className={`font-bold pl-${level * 4}`}>{mat.category}</div>
        ) : (
          // Selectable material (No children)
          <SelectItem
            value={mat.category}
            className={`text-gray-500 pl-${level * 4}`}
          >
            {mat.category}
          </SelectItem>
        )}

        {/* Render children recursively with increased indentation */}
        {mat.children.length > 0 && renderMaterials(mat.children, level + 1)}
      </div>
    ));
};

const MaterialSelect = ({ value, onChange }) => {
  const { materials, fetchMaterials } = useMaterialStore();

  useEffect(() => {
    if (!materials.length) {
      fetchMaterials();
    }
  }, []);

  // Build material hierarchy and sort alphabetically (only active materials)
  const sortedMaterials = useMemo(() => {
    const hierarchy = buildHierarchy(materials);
    return hierarchy.sort((a, b) =>
      a.category.localeCompare(b.category, "en", { sensitivity: "base" })
    );
  }, [materials]);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder="Select Material Category" />
      </SelectTrigger>
      <SelectContent>
        {sortedMaterials.length > 0 ? (
          renderMaterials(sortedMaterials)
        ) : (
          <div className="p-2 text-sm text-gray-500">
            No active materials found
          </div>
        )}
      </SelectContent>
    </Select>
  );
};

export default MaterialSelect;
